/**
 * SyncJobResultModal — pełny podgląd wyniku JEDNEGO przebiegu z historii synchronizacji.
 *
 * Otwierany po kliknięciu wiersza w `SyncJobsHistoryPanel`. Renderuje ten sam wspólny widok co
 * okna importu/eksportu/synchronizacji: siatka klikalnych kafelków + tabela rekordów z szukajką
 * nad każdą kolumną (`SyncResultPanel`), a pod spodem log ostrzeżeń/błędów (styl Symfonii).
 *
 * Dane bierze z zapisanego `job.result` (stats + details + warnings) — kubełki i etykiety składa
 * `buildResultTiles` zależnie od rodzaju operacji (import / eksport / synchronizacja dwukier.).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/Modal';

import { buildLogEntries, formatWhen, syncEntity, syncOp } from './sync-history-helpers';
import {
  type SyncRecordDetail,
  SyncResultPanel,
  type SyncResultTile,
  type SyncTileTone,
} from './SyncResultPanel';

interface SyncJobLike {
  id: string;
  jobType: string;
  result: Record<string, unknown> | null;
  /** Znaczniki czasu przebiegu (do nagłówka: data + godzina). Preferujemy `finishedAt`. */
  createdAt?: string | null;
  finishedAt?: string | null;
}

const TILE_PREFIX = 'tenant.settings.integrations.syncHistory.resultTiles';

type TileTone = SyncTileTone;

/** Buduje kafelki wyniku z zapisanego `result` zależnie od rodzaju operacji. */
function buildResultTiles(
  job: SyncJobLike,
  t: (k: string) => string,
): { tiles: SyncResultTile[]; linkBase: string } {
  const result = (job.result ?? {}) as Record<string, unknown>;
  const stats = (result.stats ?? {}) as Record<string, number>;
  const details = (result.details ?? {}) as Record<string, SyncRecordDetail[]>;
  const op = syncOp(job.jobType).key;
  const linkBase = /employee|pracownik/i.test(job.jobType) ? '/employees' : '/contractors';

  // Słowniki (pull-only) mają WŁASNE liczniki (pozycje słownikowe, nie rekordy kartotek) — bez
  // drill-downu (dane referencyjne). Osobna gałąź, żeby nie pokazywać zerowych kafelków kontrahentów.
  if (syncEntity(job.jobType) === 'dictionaries') {
    const dtile = (
      key: string,
      statKey: string,
      tone: TileTone,
      records?: SyncRecordDetail[],
    ): SyncResultTile => ({
      key,
      label: t(`${TILE_PREFIX}.${key}`),
      count: stats[statKey] ?? 0,
      tone,
      records,
    });
    return {
      linkBase,
      tiles: [
        // „Słowniki" ma drill-down = lista słowników (nazwa + klucz FK + liczby pozycji).
        dtile('dictSynced', 'dictionaries_synced', 'neutral', details.dictionaries ?? []),
        dtile('dictItemsAdded', 'items_added', 'added'),
        dtile('dictItemsUpdated', 'items_updated', 'updated'),
        dtile('dictItemsDeactivated', 'items_deactivated', 'muted'),
        dtile('dictSkippedMappings', 'skipped_mappings', 'warning'),
      ],
    };
  }

  const tile = (
    key: string,
    statKey: string,
    tone: TileTone,
    bucketKey?: string,
  ): SyncResultTile => ({
    key,
    label: t(`${TILE_PREFIX}.${key}`),
    count: stats[statKey] ?? 0,
    tone,
    records: bucketKey ? (details[bucketKey] ?? []) : undefined,
  });

  if (op === 'sync') {
    // Synchronizacja dwukierunkowa: faza detect (wykrycie) albo apply (zastosowanie).
    if (result.phase === 'apply') {
      return {
        linkBase,
        tiles: [
          tile('sent', 'pushedToFk', 'updated', 'sent'),
          tile('pulled', 'updatedLocal', 'added', 'pulled'),
          tile('ignored', 'ignored', 'muted'),
          tile('skipped', 'skipped', 'danger', 'skipped'),
        ],
      };
    }
    const conflicts =
      stats.conflictedContractors ?? stats.conflictedEmployees ?? stats.conflicts ?? 0;
    const tiles: SyncResultTile[] = [
      tile('sent', 'pushedToFk', 'updated', 'sent'),
      tile('pulled', 'updatedLocal', 'added', 'pulled'),
      { key: 'conflicts', label: t(`${TILE_PREFIX}.conflicts`), count: conflicts, tone: 'warning' },
    ];
    if (stats.linked !== undefined) tiles.push(tile('linked', 'linked', 'muted', 'linked'));
    tiles.push(tile('unchanged', 'unchanged', 'muted', 'unchanged'));
    tiles.push(tile('excluded', 'excluded', 'muted', 'excluded'));
    if (stats.unmatched !== undefined)
      tiles.push(tile('unmatched', 'unmatched', 'muted', 'unmatched'));
    tiles.push(tile('skipped', 'skipped', 'danger', 'skipped'));
    return { linkBase, tiles };
  }

  if (op === 'export') {
    return {
      linkBase,
      tiles: [
        tile('added', 'added', 'added', 'added'),
        tile('updated', 'updated', 'updated', 'updated'),
        tile('skipped', 'skipped', 'danger', 'skipped'),
        tile('excluded', 'excluded', 'muted', 'excluded'),
      ],
    };
  }

  // Import (default): dane pobrane z Symfonii do nas.
  return {
    linkBase,
    tiles: [
      tile('added', 'added', 'added', 'added'),
      tile('updated', 'updated', 'updated', 'updated'),
      tile('unchanged', 'unchanged', 'muted', 'unchanged'),
      tile('skipped', 'skipped', 'danger', 'skipped'),
      tile('inactive', 'inactive', 'muted', 'inactive'),
      tile('gusFailures', 'gusFailures', 'warning', 'gusFailures'),
    ],
  };
}

export function SyncJobResultModal({
  job,
  onClose,
}: {
  job: SyncJobLike | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Zamknięcie (job = null) zeruje rozszerzenie — kolejne otwarcie startuje w zwykłym rozmiarze.
  useEffect(() => {
    if (!job) setExpanded(false);
  }, [job]);
  if (!job) return null;

  const { tiles: baseTiles, linkBase } = buildResultTiles(job, t);
  // Ostrzeżenia i błędy też jako kafelki (spójnie z resztą) → klik pokazuje je w tabelce.
  // Zmiany („info") pomijamy — są już pod kafelkiem „zaktualizowane".
  const logEntries = buildLogEntries(job.result);
  const toRecords = (sev: 'warning' | 'error'): SyncRecordDetail[] =>
    logEntries
      .filter((e) => e.sev === sev)
      .map((e) => ({ record: e.record ?? '—', reason: e.text }));
  const warnings = toRecords('warning');
  const errors = toRecords('error');
  const tiles: SyncResultTile[] = [
    ...baseTiles,
    {
      key: 'warnings',
      label: t(`${TILE_PREFIX}.warnings`),
      count: warnings.length,
      tone: 'warning',
      records: warnings,
    },
    {
      key: 'errors',
      label: t(`${TILE_PREFIX}.errors`),
      count: errors.length,
      tone: 'danger',
      records: errors,
    },
  ];
  const opLabel = t(`tenant.settings.integrations.syncHistory.op.${syncOp(job.jobType).key}`);
  const entityLabel = t(
    `tenant.settings.integrations.syncHistory.entity.${syncEntity(job.jobType)}`,
  );
  const whenLabel = formatWhen(job.finishedAt ?? job.createdAt ?? null);

  return (
    <Modal
      open
      onClose={onClose}
      title={t('tenant.settings.integrations.syncHistory.resultModal.title', {
        op: opLabel,
        defaultValue: opLabel,
      })}
      // Podtytuł: jakiej kartoteki dotyczy + operacja + data i godzina przebiegu.
      description={
        <span data-testid="sync-job-result-subtitle">
          {entityLabel} · {opLabel} · {whenLabel}
        </span>
      }
      size="lg"
      expandable
      expanded={expanded}
      onExpandedChange={setExpanded}
      testId="sync-job-result-modal"
    >
      <div className="flex h-full min-h-0 flex-col">
        <SyncResultPanel
          tiles={tiles}
          onNavigate={onClose}
          linkBase={linkBase}
          testId="history-result"
          fillHeight={expanded}
          listHeightClass="h-72"
        />
      </div>
    </Modal>
  );
}

// Re-export dla testów jednostkowych logiki kafelków.
export { buildResultTiles };
